import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import view_icon from "../../assets/images/view_icon.svg";
import edit_icon from "../../assets/images/edit_icon.svg";
import delete_icon from "../../assets/images/delete_icon.svg";

import DeletePopup from "../popups/DeletePopup";
import { deleteCompany } from "../../store/slices/userSlice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import user from "../../assets/images/User.png";
import { formatDate, formatCurrency } from "../../utils/dateUtils";
import ProfileDetail from "../layout/ProfileDetail";

const CompanyListTable = ({
  className,
  data = [],
  isDetailPage,
  loading = false,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
  pageKey,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const totalPages = Math.ceil(totalRecords / pageSize);

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedItem?.id) {
      dispatch(
        deleteCompany({
          id: selectedItem.id,
          key: pageKey,
        }),
      );

      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const formatPrettyDate = (dateString) => {
    return formatDate(dateString);
  };

  const openProfile = (item) => {
    setSelectedUserId(item.id);
    setShowProfileModal(true);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";

    if (phone.trim().startsWith("+")) {
      return phone;
    }

    const digits = phone.replace(/\D/g, "");
    return digits.length >= 10 ? digits.slice(-10) : digits;
  };

  return (
    <>
      <div className={`custom_table table-responsive ${className || ""}`}>
        <div className="table-wrapper">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Email</th>
                {!isDetailPage && <th>Client</th>}
                <th>Phone No</th>
                <th>Employees</th>
                <th>Registered On</th>
                <th>Pay Avg</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center">
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <div
                        className="spinner-border text-primary me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading</span>
                      </div>
                      Loading
                    </div>
                  </td>
                </tr>
              ) : Array.isArray(data) && data.length > 0 ? (
                data.map((item) => {
                  const status = item?.isActive ? "Active" : "Inactive";
                  const companyId = item?.id || item?.companyId;

                  return (
                    <tr key={companyId}>
                      {/* Company Name */}
                      <td>
                        <div
                          className="d-flex align-items-center cursor-pointer"
                          onClick={() =>
                            navigate(`/companydetailpage/${companyId}`)
                          }
                        >
                          <div className="user_img me-2">
                            <img
                              src={
                                item?.logo !== null
                                  ? BASE_URL + item.logo
                                  : user
                              }
                              alt="company"
                            />
                          </div>
                          {item?.companyName || "N/A"}
                        </div>
                      </td>

                      <td
                        className="truncated-text"
                        data-full-text={item?.email}
                      >
                        {item?.email && item.email.length > 30
                          ? item.email.substring(0, 30) + "..."
                          : item?.email || "N/A"}
                      </td>
                      {!isDetailPage && <td>{item?.clientName || "N/A"}</td>}
                      <td>{formatPhoneNumber(item?.phone)}</td>
                      <td>{item?.employeeCount ?? 0}</td>
                      <td>{formatPrettyDate(item?.registeredOn)}</td>
                      <td>{formatCurrency(item?.payAvg)}</td>

                      {/* Status */}
                      <td>
                        <div className={status.toLowerCase()}>{status}</div>
                      </td>

                      {/* Actions (NO BUTTONS → NO HIGHLIGHT) */}
                      <td>
                        <div className="action_links d-flex gap-2">
                          <span
                            className="icon_btn"
                            role="button"
                            title="View"
                            onClick={() => openProfile(item)}
                          >
                            <img src={view_icon} alt="view" />
                          </span>
                          <span
                            className="icon_btn"
                            role="button"
                            title="Edit"
                            onClick={() =>
                              navigate(`/editcompany`, {
                                state: {
                                  id: companyId,
                                  from: location.pathname,
                                },
                              })
                            }
                          >
                            <img src={edit_icon} alt="edit" />
                          </span>

                          <span
                            className="icon_btn"
                            role="button"
                            title="Delete"
                            onClick={() => handleDelete(item)}
                          >
                            <img src={delete_icon} alt="delete" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="text-center">
                    No Company Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION FOOTER */}
        {totalRecords > 0 && (
          <div className="table_footer">
            <div className="footer_left">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}{" "}
              entries
            </div>

            <div className="footer_right">
              <button
                className="page_btn"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
              >
                ‹ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page_btn ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                  onClick={() => onPageChange(i + 1)}
                >
                  {String(i + 1).padStart(2, "0")}
                </button>
              ))}

              <button
                className="page_btn"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      <DeletePopup
        show={showDeleteModal}
        type="Company"
        handleClose={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        allowedNames={selectedItem ? [selectedItem.companyName] : []}
      />

      {selectedUserId && (
        <ProfileDetail
          show={showProfileModal}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedUserId(null);
          }}
          clientId={selectedUserId}
          type="company"
        />
      )}
    </>
  );
};

export default CompanyListTable;
